/*!
 * cart-inquiry.js
 * Shopify (Empire) cart -> GAS (POST) for inquiry/quote request.
 *
 * Required form markup (example):
 * <form class="js-cart-inquiry" data-gas-endpoint="https://script.google.com/macros/s/XXXX/exec" data-thanks-url="/pages/thanks" data-clear-cart="1">
 *   <input name="customer_name" required>
 *   <input name="customer_email" type="email" required>
 *   <textarea name="message"></textarea>
 *   <input type="hidden" name="company" value="">  // honeypot (must stay empty)
 *   <input type="hidden" name="cart_json" value="">
 *   <button type="submit">送信</button>
 * </form>
 */

(function () {
  'use strict';

  // -----------------------------
  // Settings (optional defaults)
  // -----------------------------
  // 同じメールが2通届くのを防ぐ：全体で1リクエストだけ許可
  var isSubmitting = false;

  const DEFAULTS = {
    // You can set a fallback endpoint here, but data-gas-endpoint on <form> is recommended.
    gasEndpoint: '',
    // Optional: thank-you page URL (can also be provided via data-thanks-url on the form)
    thanksUrl: '',
    // Messages (you can change)
    msgSending: '送信中…',
    msgSuccess: '送信しました。受付完了メールをご確認ください。',
    msgError: '送信に失敗しました。時間をおいて再度お試しください。',
    clearCartOnSuccess: false,
  };

  // -----------------------------
  // Utilities
  // -----------------------------
  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function setBusy(form, isBusy) {
    form.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    const btn = $('button[type="submit"], input[type="submit"]', form);
    if (btn) btn.disabled = !!isBusy;
  }

  function setStatus(form, text, kind) {
    // kind: 'info' | 'success' | 'error'
    let box = $('.js-cart-inquiry-status', form);
    if (!box) {
      box = document.createElement('div');
      box.className = 'js-cart-inquiry-status';
      box.style.marginTop = '0.75rem';
      box.style.fontSize = '0.95rem';
      form.appendChild(box);
    }
    const raw = text || '';
    const escaped = String(raw)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Allow line breaks via "\n" and "<br>" only.
    box.innerHTML = escaped
      .replace(/&lt;br\s*\/?&gt;/gi, '<br>')
      .replace(/\n/g, '<br>');
    // minimal visual hint (no theme CSS dependency)
    box.style.color =
      kind === 'success' ? '#0a7a2f' :
      kind === 'error' ? '#b00020' :
      '';
  }

  function safeJsonParse(str, fallback) {
    try { return JSON.parse(str); } catch (e) { return fallback; }
  }

  function buildCartPayload(cart, cartNote) {
    const items = (cart.items || []).map((it) => ({
      title: it.product_title || it.title || '',
      variant_title: it.variant_title || '',
      quantity: it.quantity || 0,
      sku: it.sku || '',
      product_id: it.product_id ?? null,
      variant_id: it.variant_id ?? null,
      price: it.price ?? null,       // 単価（最小通貨単位）
      line_price: it.line_price ?? null, // 行合計（最小通貨単位）
      url: it.url || '',
    }));

    const note = (typeof cartNote === 'string' ? cartNote.trim() : '') || (cart.note || '').trim();

    return {
      origin: (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '',
      currency: cart.currency || 'JPY',
      item_count: cart.item_count || 0,
      total_price: cart.total_price ?? null, // minor unit
      note: note,
      items,
    };
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch /cart.js');
    return res.json();
  }

  function getGasEndpoint(form) {
    return (form.dataset && form.dataset.gasEndpoint) ? form.dataset.gasEndpoint : DEFAULTS.gasEndpoint;
  }

  function getThanksUrl(form) {
    return (form.dataset && form.dataset.thanksUrl)
      ? form.dataset.thanksUrl
      : DEFAULTS.thanksUrl;
  }

  function redirectToThanksIfNeeded(form) {
    const url = getThanksUrl(form);
    if (!url) return;

    // Allow relative paths like /pages/thanks
    try {
      // Delay a tick so status text is visible briefly
      window.setTimeout(() => {
        window.location.href = url;
      }, 150);
    } catch (e) {
      // noop
    }
  }

  function getField(form, name) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return form.querySelector(`[name="${CSS.escape(name)}"]`);
    } else {
      // Fallback: naive selector, may fail on special chars
      return form.querySelector(`[name="${name}"]`);
    }
  }

  function ensureHiddenField(form, name) {
    let el = getField(form, name);
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      form.appendChild(el);
    }
    return el;
  }

  function shouldClearCart(form) {
    if (form.dataset && typeof form.dataset.clearCart !== 'undefined') {
      return form.dataset.clearCart === '1' || form.dataset.clearCart.toLowerCase() === 'true';
    }
    return DEFAULTS.clearCartOnSuccess;
  }

  async function clearCart() {
    // Clear cart by posting to /cart/clear.js
    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (e) {
      // Fail silently
    }
  }

  function collectFormFields(form) {
    const nameEl = getField(form, 'customer_name');
    const emailEl = getField(form, 'customer_email');
    const msgEl = getField(form, 'message');
    const honeypotEl = getField(form, 'company'); // must exist, should remain empty

    return {
      customer_name: nameEl ? nameEl.value.trim() : '',
      customer_email: emailEl ? emailEl.value.trim() : '',
      message: msgEl ? msgEl.value.trim() : '',
      company: honeypotEl ? honeypotEl.value : '', // should be ''
    };
  }

  function validate(fields) {
    const name = (fields.customer_name || '').trim();
    const email = (fields.customer_email || '').trim();
    const errormessage = 'エラーのため送信できませんでした<br>営業担当までお問い合わせください';
    if (!name && !email) {
      return { ok: false, error: errormessage };
    }
    if (!name) return { ok: false, error: errormessage };
    if (!email) return { ok: false, error: errormessage };
    // very light email check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { ok: false, error: 'メールアドレスの形式が正しくありません。' };
    }
    return { ok: true };
  }

  function submitViaHiddenIframe(form, endpoint) {
    // Fallback that avoids CORS by using a classic form POST into a hidden iframe.
    // Note: We cannot reliably read the response; we optimistically show success.
    const iframeId = 'js-cart-inquiry-frame';
    let iframe = document.getElementById(iframeId);
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = iframeId;
      iframe.name = iframeId;
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);
    }

    const prev = {
      action: form.getAttribute('action') || '',
      method: form.getAttribute('method') || '',
      target: form.getAttribute('target') || '',
    };

    form.setAttribute('action', endpoint);
    form.setAttribute('method', 'post');
    form.setAttribute('target', iframeId);

    try {
      form.submit();
    } finally {
      // Restore attributes to avoid side-effects
      if (prev.action) form.setAttribute('action', prev.action); else form.removeAttribute('action');
      if (prev.method) form.setAttribute('method', prev.method); else form.removeAttribute('method');
      if (prev.target) form.setAttribute('target', prev.target); else form.removeAttribute('target');
    }

    return { ok: true, fallback: true };
  }

  async function postToGAS(endpoint, fields, cartPayload) {
    // Send as x-www-form-urlencoded to avoid CORS preflight issues as much as possible.
    const bodyParams = new URLSearchParams();
    bodyParams.set('customer_name', fields.customer_name);
    bodyParams.set('customer_email', fields.customer_email);
    bodyParams.set('message', fields.message);
    bodyParams.set('company', fields.company || '');
    bodyParams.set('cart_json', JSON.stringify(cartPayload));

    let res;
    try {
      res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: bodyParams.toString(),
      });
    } catch (e) {
      // Typical causes: CSP connect-src blocked, CORS/network error, wrong endpoint URL, adblocker, mixed content.
      throw new Error('fetchに失敗しました（CSP/通信/CORS/URLの可能性）');
    }

    // Prefer JSON, but many failures return HTML (permission page) or an unreadable body (CORS).
    // Read as text first so we can diagnose.
    let text = '';
    try {
      text = await res.text();
    } catch (e) {
      // When CORS blocks the response body, reading can fail.
      throw new Error('レスポンスを読み取れませんでした（CORSの可能性）');
    }

    const json = safeJsonParse(text, null);
    if (!json) {
      // Not JSON => likely HTML (wrong URL, not /exec, permission, etc.)
      const isHtml = /<\s*html/i.test(text);
      const hint = isHtml
        ? 'HTMLが返っています（GASのデプロイ権限/URLが /exec か確認）'
        : 'JSON以外のレスポンスです（GAS URL/権限を確認）';
      throw new Error(`${hint} / status=${res.status}`);
    }

    if (json.ok !== true) {
      throw new Error(json.error || `GAS returned error / status=${res.status}`);
    }

    return json;
  }

  async function handleSubmit(e, form) {
    e.preventDefault();

    // 二重送信防止（全体ロック + フォーム単位）
    if (isSubmitting) return;
    if (form.dataset && form.dataset.submitting === '1') return;
    isSubmitting = true;
    form.dataset.submitting = '1';

    const endpoint = getGasEndpoint(form);
    if (!endpoint) {
      isSubmitting = false;
      if (form.dataset) delete form.dataset.submitting;
      setStatus(form, '送信先（GAS URL）が未設定です。data-gas-endpoint を設定してください。', 'error');
      return;
    }

    const fields = collectFormFields(form);
    const v = validate(fields);
    if (!v.ok) {
      isSubmitting = false;
      if (form.dataset) delete form.dataset.submitting;
      setStatus(form, v.error, 'error');
      return;
    }

    try {
      setBusy(form, true);
      setStatus(form, DEFAULTS.msgSending, 'info');

      // Always fetch latest cart at submit time (handles drawer/ajax updates)
      const cart = await fetchCart();
      // 注文メモ: 画面の #cart-note を優先（未保存の入力も送る）
      const noteEl = document.getElementById('cart-note');
      const cartNote = noteEl ? noteEl.value : (cart.note || '');
      const payload = buildCartPayload(cart, cartNote);

      // Optional: fill hidden cart_json field if exists (for debugging / fallback)
      const cartJsonEl = ensureHiddenField(form, 'cart_json');
      if (cartJsonEl) cartJsonEl.value = JSON.stringify(payload);

      try {
        await postToGAS(endpoint, fields, payload);
        setStatus(form, DEFAULTS.msgSuccess, 'success');
        if (shouldClearCart(form)) {
          try {
            await clearCart();
          } catch (e) {
            // ignore clear error and continue redirect
          }
        }
        redirectToThanksIfNeeded(form);
      } catch (innerErr) {
        const msg = (innerErr && innerErr.message) ? innerErr.message : String(innerErr);
        // If fetch itself is blocked (CSP/CORS), try classic form POST fallback.
        if (/(Failed to fetch|fetchに失敗)/i.test(msg)) {
          submitViaHiddenIframe(form, endpoint);
          setStatus(form, DEFAULTS.msgSuccess , 'success');
          if (shouldClearCart(form)) {
            try {
              await clearCart();
            } catch (e) {
              // ignore clear error and continue redirect
            }
          }
          redirectToThanksIfNeeded(form);
        } else {
          throw innerErr;
        }
      }

      // Optional: reset message only (keep name/email)
      const msgEl = getField(form, 'message');
      if (msgEl) msgEl.value = '';

    } catch (err) {
      console.error(err);
      const detail = (err && err.message) ? err.message : String(err);
      setStatus(form, `${DEFAULTS.msgError}\n(${detail})`, 'error');
    } finally {
      isSubmitting = false;
      setBusy(form, false);
      if (form.dataset) delete form.dataset.submitting;
    }
  }

  function bindForms(root) {
    const forms = $all('form.js-cart-inquiry', root);
    forms.forEach((form) => {
      if (form.dataset && form.dataset.bound === '1') return;
      form.dataset.bound = '1';

      form.addEventListener('submit', (e) => handleSubmit(e, form));
    });
  }

  // Initial bind (cart page)
  bindForms(document);

  // Bind again on dynamic DOM changes (cart drawer updates, etc.)
  // Lightweight MutationObserver
  const mo = new MutationObserver(() => {
    bindForms(document);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

})();